import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import type { BuilderMode } from '@/api/questionnaires/types';
import { PreviewPage } from '@/app/(client)/questionnaires/-components/PreviewPage';
import { builderDetailQueryOptions } from '@/app/(client)/questionnaires/-hooks/useBuilderDetailQuery';
import { queryClient } from '@/lib/utils/queryClient';

const previewSearch = z.object({
  type: z.enum(['questionnaire', 'template']).optional(),
  // Accept both string and boolean — TanStack Router's default parser
  // auto-coerces `?isTemplate=true` to a boolean, but legacy callers
  // may still pass the literal string.
  isTemplate: z.union([z.string(), z.boolean()]).optional(),
  /** When truthy, the preview swaps the live detail for a hand-crafted
   *  mock that exercises every supported answer type. Accepts either
   *  `?mock=true` (string from URL) or a boolean flag pushed via
   *  `navigate({ search: { mock: true } })`. */
  mock: z.union([z.string(), z.boolean()]).optional(),
});

function isMockOn(mock: string | boolean | undefined): boolean {
  if (mock === true) return true;
  if (typeof mock === 'string') return mock === 'true' || mock === '1';
  return false;
}

function resolveMode(search: {
  type?: 'questionnaire' | 'template' | undefined;
  isTemplate?: string | boolean | undefined;
}): BuilderMode {
  const isTemplate = search.isTemplate === true || search.isTemplate === 'true';
  if (search.type === 'template' || isTemplate) {
    return 'template';
  }
  return 'questionnaire';
}

export const Route = createFileRoute(
  '/(client)/questionnaires/(pages)/(detail)/$id_/preview/',
)({
  validateSearch: previewSearch,
  loaderDeps: ({ search }) => ({
    mode: resolveMode(search),
    isMock: isMockOn(search.mock),
  }),
  loader: ({ params, deps }) => {
    // Skip the prefetch in mock mode — the page swaps in synthetic data.
    if (deps.isMock) return;
    return queryClient.ensureQueryData(
      builderDetailQueryOptions(deps.mode, params.id),
    );
  },
  component: QuestionnairePreviewRoute,
});

function QuestionnairePreviewRoute() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const mode = resolveMode(search);
  const navigate = useNavigate();
  const isMock = isMockOn(search.mock);

  return (
    <PreviewPage
      mode={mode}
      id={id}
      isMock={isMock}
      onExit={() =>
        void navigate({ to: '/questionnaires/$id/edit', params: { id } })
      }
    />
  );
}
